/**
 * Privacy Policy Route
 * Comprehensive privacy policy page
 */

import { createFileRoute } from '@tanstack/react-router';
import { SEOHead } from '@/components/seo/SEOHead';
import { StructuredData, generateBreadcrumbSchema } from '@/components/seo/StructuredData';
import { PageLayout } from '@/components/layout/PageLayout';

function PrivacyPolicyPage() {
  const breadcrumbData = generateBreadcrumbSchema([
    { name: "Home", url: "https://invoicegenerated.com/" },
    { name: "Privacy Policy", url: "https://invoicegenerated.com/privacy" }
  ]);

  return (
    <>
      <SEOHead
        title="Privacy Policy - Smart Invoice Generator"
        description="Privacy Policy for Smart Invoice Generator. Learn how we protect your data, what information we collect, and how we ensure your invoice data remains private and secure."
        keywords="privacy policy, data protection, invoice privacy, user data security, GDPR compliance, data collection practices"
        canonical="https://invoicegenerated.com/privacy"
      />
      <StructuredData data={breadcrumbData} />

      <PageLayout>
        <div className="w-full px-4 sm:px-6 lg:px-8 py-12 max-w-4xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 sm:p-12">
            {/* Page Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Privacy Policy
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-8">
              Last Updated: February 4, 2026
            </p>

            {/* Introduction */}
            <section className="mb-10">
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                At Smart Invoice Generator, we take your privacy seriously. This Privacy Policy explains how we handle, protect, and respect your personal information when you use our invoice generation service.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We believe in transparency and want you to understand exactly what happens with your data. By using Smart Invoice Generator, you agree to the practices described in this policy.
              </p>
              <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-4 rounded-r">
                <p className="text-green-900 dark:text-green-200 font-semibold">
                  Good news: We don't store your invoice data on our servers!
                </p>
              </div>
            </section>

            {/* Table of Contents */}
            <section className="mb-10 bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Table of Contents
              </h2>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li><a href="#information-we-collect" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">1. Information We Collect</a></li>
                <li><a href="#how-we-use-information" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">2. How We Use Your Information</a></li>
                <li><a href="#data-storage-security" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">3. Data Storage and Security</a></li>
                <li><a href="#third-party-services" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">4. Third-Party Services</a></li>
                <li><a href="#cookies-tracking" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">5. Cookies and Tracking</a></li>
                <li><a href="#user-rights" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">6. Your Rights and Choices</a></li>
                <li><a href="#data-retention" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">7. Data Retention and Deletion</a></li>
                <li><a href="#children-privacy" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">8. Children's Privacy</a></li>
                <li><a href="#international-transfers" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">9. International Data Transfers</a></li>
                <li><a href="#policy-changes" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">10. Changes to This Policy</a></li>
                <li><a href="#contact" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">11. How to Contact Us</a></li>
              </ul>
            </section>

            {/* 1. Information We Collect */}
            <section id="information-we-collect" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                1. Information We Collect
              </h2>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Information You Provide
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Smart Invoice Generator is designed to protect your privacy. The information you enter into our invoice generator forms (such as client names, addresses, invoice amounts, and line items) is:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li><strong>Processed locally in your browser</strong> - All invoice generation happens on your device using JavaScript</li>
                <li><strong>Not stored on our servers</strong> - We do not save, transmit, or store your invoice content on any backend server</li>
                <li><strong>Not shared with third parties</strong> - Your invoice data never leaves your browser except for the PDF/PNG file you download</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Automatically Collected Information
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Like most websites, we automatically collect certain technical information when you visit our site:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li><strong>Browser type and version</strong> - To ensure compatibility and improve performance</li>
                <li><strong>Operating system</strong> - To optimize our service for different platforms</li>
                <li><strong>IP address</strong> - For security purposes and approximate location detection</li>
                <li><strong>Device information</strong> - Mobile, tablet, or desktop to provide responsive design</li>
                <li><strong>Referring website</strong> - To understand how users find our service</li>
                <li><strong>Pages visited and time spent</strong> - To improve user experience and identify popular features</li>
              </ul>
            </section>

            {/* 2. How We Use Your Information */}
            <section id="how-we-use-information" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                2. How We Use Your Information
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We use the collected technical information for the following purposes:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li><strong>Service Delivery:</strong> To provide and maintain our invoice generation service</li>
                <li><strong>Performance Optimization:</strong> To improve site speed, reliability, and user experience</li>
                <li><strong>Security:</strong> To detect, prevent, and address technical issues and fraudulent activity</li>
                <li><strong>Analytics:</strong> To understand how users interact with our service and identify areas for improvement</li>
                <li><strong>Feature Development:</strong> To decide which new features to prioritize based on usage patterns</li>
              </ul>
              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded-r">
                <p className="text-blue-900 dark:text-blue-200">
                  <strong>Important:</strong> We never sell, rent, or share your personal information with third parties for marketing purposes.
                </p>
              </div>
            </section>

            {/* 3. Data Storage and Security */}
            <section id="data-storage-security" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                3. Data Storage and Security
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Your privacy and data security are our top priorities. Here's how we protect your information:
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Client-Side Processing
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                All invoice generation happens in your browser. This means:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li>Your invoice data is never transmitted to our servers</li>
                <li>No one on our team ever sees your invoice information</li>
                <li>Your data exists only temporarily in your browser's memory while you're using the tool</li>
                <li>The only permanent copy of your invoice is the PDF or PNG file you download to your device</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Local Storage (Optional)
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We may use browser localStorage to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li>Save your theme preference (light/dark mode)</li>
                <li>Save your language preference</li>
                <li>Temporarily store invoice history for your convenience</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                This data is stored only on your device and can be cleared at any time through your browser settings.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Security Measures
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We implement industry-standard security practices:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li>HTTPS encryption for all data transmission</li>
                <li>Regular security audits and updates</li>
                <li>Secure hosting infrastructure</li>
                <li>No sensitive data storage on our servers</li>
              </ul>
            </section>

            {/* 4. Third-Party Services */}
            <section id="third-party-services" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                4. Third-Party Services
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We may use third-party services to help operate our platform. These services have their own privacy policies:
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Ko-fi (Payment Processing)
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                If you choose to support us through Ko-fi, you'll be redirected to their website. Ko-fi's privacy policy governs the collection and use of your payment information. We do not have access to your payment details.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Analytics Services</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We may use analytics tools (such as Google Analytics or similar) to understand how users interact with our service. These tools may collect information about your browsing behavior. You can opt-out of analytics through browser extensions or your device settings.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Content Delivery Networks (CDNs)
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We use CDNs to deliver our service faster and more reliably. These services may log IP addresses and request metadata for security and performance optimization.
              </p>
            </section>

            {/* 5. Cookies and Tracking */}
            <section id="cookies-tracking" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                5. Cookies and Tracking Technologies
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We use cookies and similar tracking technologies to enhance your experience:
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Types of Cookies We Use
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li><strong>Essential Cookies:</strong> Required for the website to function properly (authentication, security)</li>
                <li><strong>Preference Cookies:</strong> Remember your settings (theme, language)</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how users interact with our service</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Managing Cookies
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                You can control and manage cookies through your browser settings:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li>Block all cookies (may affect website functionality)</li>
                <li>Delete existing cookies</li>
                <li>Set notifications when cookies are sent</li>
                <li>Use private/incognito browsing mode</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Note that disabling cookies may prevent certain features from working properly.
              </p>
            </section>

            {/* 6. Your Rights and Choices */}
            <section id="user-rights" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                6. Your Rights and Choices
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Depending on your location, you may have certain rights regarding your personal information:
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                GDPR Rights (EU Residents)
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                If you're located in the European Union, you have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
                <li><strong>Erasure:</strong> Request deletion of your personal data</li>
                <li><strong>Restrict Processing:</strong> Request limitation of data processing</li>
                <li><strong>Data Portability:</strong> Request transfer of your data to another service</li>
                <li><strong>Object:</strong> Object to processing of your personal data</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                CCPA Rights (California Residents)
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                If you're a California resident, you have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li><strong>Right to Know:</strong> Request information about data collection and use</li>
                <li><strong>Right to Delete:</strong> Request deletion of your personal information</li>
                <li><strong>Right to Opt-Out:</strong> Opt-out of the sale of personal data (note: we don't sell data)</li>
                <li><strong>Right to Non-Discrimination:</strong> Not be discriminated against for exercising privacy rights</li>
              </ul>

              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                To exercise these rights, please contact us using the information provided in Section 11.
              </p>
            </section>

            {/* 7. Data Retention and Deletion */}
            <section id="data-retention" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                7. Data Retention and Deletion
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Since we don't store your invoice data on our servers, retention is minimal:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li><strong>Invoice Data:</strong> Not retained on our servers (exists only in your browser temporarily)</li>
                <li><strong>Local Storage:</strong> Retained until you clear your browser cache or disable our service</li>
                <li><strong>Server Logs:</strong> Automatically deleted after a standard retention period (typically 30-90 days)</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                You can delete all locally stored data at any time by clearing your browser cache and localStorage.
              </p>
            </section>

            {/* 8. Children's Privacy */}
            <section id="children-privacy" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                8. Children's Privacy
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Smart Invoice Generator is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                If we become aware that we have collected personal information from a child under 13 without parental consent, we will take steps to remove that information.
              </p>
            </section>

            {/* 9. International Data Transfers */}
            <section id="international-transfers" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                9. International Data Transfers
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Since your invoice data is processed entirely in your browser, international data transfers are minimal. However, our technical infrastructure (hosting, CDNs) may be located in various countries. We ensure that any data transferred internationally is protected with appropriate security measures.
              </p>
            </section>

            {/* 10. Changes to This Policy */}
            <section id="policy-changes" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                10. Changes to This Privacy Policy
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li>Posting the new policy on this page with an updated "Last Updated" date</li>
                <li>Displaying a prominent notice on our website</li>
                <li>Sending an email notification if you've provided contact information (rare - we don't typically collect email addresses)</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information.
              </p>
            </section>

            {/* 11. Contact Us */}
            <section id="contact" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                11. How to Contact Us
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                  <li>
                    <strong>Email:</strong>{' '}
                    <a href="mailto:marfanw@gmail.com" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                      marfanw@gmail.com
                    </a>
                  </li>
                  <li>
                    <strong>Contact Form:</strong>{' '}
                    <a href="/contact" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                      Visit our Contact page
                    </a>
                  </li>
                </ul>
              </div>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-4">
                We will respond to your inquiry within 30 days of receipt.
              </p>
            </section>

            {/* Closing Statement */}
            <section className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg">
              <p className="text-gray-900 dark:text-gray-100 font-semibold text-lg mb-2">
                Your Privacy Matters
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                Smart Invoice Generator is committed to protecting your privacy. We've built our service with privacy-first principles, ensuring that your sensitive financial information never leaves your device unless you choose to share it. Thank you for trusting us with your invoicing needs.
              </p>
            </section>
          </div>
        </div>
      </PageLayout>
    </>
  );
}

export const Route = createFileRoute('/privacy')({
  component: PrivacyPolicyPage,
});
